/**
 * useCardFieldsDraft — manages draft state for the card fields editor.
 *
 * Extracts all draft state + mutation handlers that were previously inlined
 * in `CardFieldsPopoverContent`. Both the modal (CardFieldsPopover) and the
 * slideout tab (StagesSlideout) use this hook.
 */

import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { PipelineCardConfig } from '../utils/cardConfig';
import {
  addFallback,
  addRow,
  addSlot,
  moveRow,
  removeFallback,
  removeRow,
  removeSlot,
  setFallbackField,
  setSlotField,
  toggleSlotLabel,
} from '../utils/cardConfigMutations';
import type { CardFieldRef } from '../utils/cardConfigOptions';

export interface CardFieldsDraftResult {
  draft: PipelineCardConfig;
  rowIds: string[];
  announcement: string;
  isDirty: boolean;
  addButtonRef: React.RefObject<HTMLButtonElement | null>;
  getDraft: () => PipelineCardConfig;
  /**
   * Replace the draft AND reset the dirty baseline to the new config.
   * Use when adopting a config as the new clean state (e.g. on open or
   * after a successful save).
   */
  resetDraft: (next: PipelineCardConfig) => void;
  /**
   * Replace the draft config WITHOUT moving the dirty baseline and WITHOUT
   * persisting. Used by "Reset to default": the preview updates and the
   * editor becomes dirty (if the default differs from the persisted config),
   * so the change is only committed when the user clicks Save.
   */
  replaceDraft: (next: PipelineCardConfig) => void;
  handleAddRow: () => void;
  handleRemoveRow: (rowIndex: number) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleSetSlotField: (
    rowIndex: number,
    slotIndex: number,
    field: CardFieldRef
  ) => void;
  handleAddSlot: (rowIndex: number) => void;
  handleRemoveSlot: (rowIndex: number, slotIndex: number) => void;
  handleAddFallback: (rowIndex: number, slotIndex: number) => void;
  handleSetFallbackField: (
    rowIndex: number,
    slotIndex: number,
    fallbackIndex: number,
    field: CardFieldRef
  ) => void;
  handleRemoveFallback: (
    rowIndex: number,
    slotIndex: number,
    fallbackIndex: number
  ) => void;
  handleToggleSlotLabel: (rowIndex: number, slotIndex: number) => void;
}

export function useCardFieldsDraft(
  initialConfig: PipelineCardConfig
): CardFieldsDraftResult {
  const [draft, setDraft] = useState<PipelineCardConfig>(() => initialConfig);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // Stable snapshot of the initial config for dirty checking. Uses a ref
  // so the initial value is captured once and doesn't change when the
  // parent re-renders with a new config reference (same content).
  const initialConfigRef = useRef(initialConfig);
  const initialJson = useRef(JSON.stringify(initialConfig));

  const [rowIds, setRowIds] = useState<string[]>(() =>
    initialConfig.rows.map(() => crypto.randomUUID())
  );
  const rowIdsRef = useRef(rowIds);
  rowIdsRef.current = rowIds;

  const [announcement, setAnnouncement] = useState('');
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== initialJson.current,
    [draft]
  );

  const apply = useCallback((next: PipelineCardConfig) => {
    setDraft(next);
  }, []);

  // Flush + re-set so identical consecutive messages are re-announced.
  const announce = useCallback((msg: string) => {
    setAnnouncement('');
    requestAnimationFrame(() => setAnnouncement(msg));
  }, []);

  // ── Structural handlers (update rowIds) ─────────────────────────────

  const handleAddRow = useCallback(() => {
    const next = addRow(draftRef.current);
    setRowIds(ids => [...ids, crypto.randomUUID()]);
    apply(next);
    announce('Row added');
  }, [apply, announce]);

  const handleRemoveRow = useCallback(
    (rowIndex: number) => {
      const next = removeRow(draftRef.current, rowIndex);
      setRowIds(ids => ids.filter((_, i) => i !== rowIndex));
      apply(next);
      announce('Row removed');
      requestAnimationFrame(() => {
        addButtonRef.current?.focus();
      });
    },
    [apply, announce]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const currentIds = rowIdsRef.current;
      const fromIndex = currentIds.indexOf(active.id as string);
      const toIndex = currentIds.indexOf(over.id as string);
      if (fromIndex === -1 || toIndex === -1) {
        return;
      }
      const next = moveRow(draftRef.current, fromIndex, toIndex);
      setRowIds(arrayMove(currentIds, fromIndex, toIndex));
      apply(next);
      announce(`Row moved to position ${toIndex + 1}`);
    },
    [apply, announce]
  );

  // ── Content-only handlers (do NOT touch rowIds) ─────────────────────

  const handleSetSlotField = useCallback(
    (rowIndex: number, slotIndex: number, field: CardFieldRef) => {
      const next = setSlotField(draftRef.current, rowIndex, slotIndex, field);
      apply(next);
    },
    [apply]
  );

  const handleAddSlot = useCallback(
    (rowIndex: number) => {
      const next = addSlot(draftRef.current, rowIndex);
      apply(next);
    },
    [apply]
  );

  const handleRemoveSlot = useCallback(
    (rowIndex: number, slotIndex: number) => {
      const before = draftRef.current;
      const next = removeSlot(before, rowIndex, slotIndex);
      if (next.rows.length < before.rows.length) {
        setRowIds(ids => ids.filter((_, i) => i !== rowIndex));
        announce('Row removed');
        requestAnimationFrame(() => {
          addButtonRef.current?.focus();
        });
      }
      apply(next);
    },
    [apply, announce]
  );

  const handleAddFallback = useCallback(
    (rowIndex: number, slotIndex: number) => {
      const next = addFallback(draftRef.current, rowIndex, slotIndex);
      apply(next);
    },
    [apply]
  );

  const handleSetFallbackField = useCallback(
    (
      rowIndex: number,
      slotIndex: number,
      fallbackIndex: number,
      field: CardFieldRef
    ) => {
      const next = setFallbackField(
        draftRef.current,
        rowIndex,
        slotIndex,
        fallbackIndex,
        field
      );
      apply(next);
    },
    [apply]
  );

  const handleRemoveFallback = useCallback(
    (rowIndex: number, slotIndex: number, fallbackIndex: number) => {
      const next = removeFallback(
        draftRef.current,
        rowIndex,
        slotIndex,
        fallbackIndex
      );
      apply(next);
    },
    [apply]
  );

  const handleToggleSlotLabel = useCallback(
    (rowIndex: number, slotIndex: number) => {
      const next = toggleSlotLabel(draftRef.current, rowIndex, slotIndex);
      apply(next);
    },
    [apply]
  );

  const resetDraft = useCallback((next: PipelineCardConfig) => {
    setDraft(next);
    draftRef.current = next;
    initialConfigRef.current = next;
    initialJson.current = JSON.stringify(next);
    setRowIds(next.rows.map(() => crypto.randomUUID()));
    setAnnouncement('');
  }, []);

  // Like resetDraft but leaves the dirty baseline untouched, so the editor
  // reflects the new config as an unsaved edit rather than a clean state.
  const replaceDraft = useCallback((next: PipelineCardConfig) => {
    setDraft(next);
    draftRef.current = next;
    setRowIds(next.rows.map(() => crypto.randomUUID()));
    setAnnouncement('');
  }, []);

  return useMemo(
    () => ({
      draft,
      rowIds,
      announcement,
      isDirty,
      addButtonRef,
      getDraft: () => draftRef.current,
      resetDraft,
      replaceDraft,
      handleAddRow,
      handleRemoveRow,
      handleDragEnd,
      handleSetSlotField,
      handleAddSlot,
      handleRemoveSlot,
      handleAddFallback,
      handleSetFallbackField,
      handleRemoveFallback,
      handleToggleSlotLabel,
    }),
    [
      draft,
      rowIds,
      announcement,
      isDirty,
      resetDraft,
      replaceDraft,
      handleAddRow,
      handleRemoveRow,
      handleDragEnd,
      handleSetSlotField,
      handleAddSlot,
      handleRemoveSlot,
      handleAddFallback,
      handleSetFallbackField,
      handleRemoveFallback,
      handleToggleSlotLabel,
    ]
  );
}
